import { Key } from "react";
import { useFormikContext } from "formik";
import { Tooltip } from "react-tooltip";
import { object } from "yup";
import Stepper from "../../components/Stepper/Stepper.tsx";
import url from "../../module/fetch.tsx";
import { requiredFile } from "../../module/validators.tsx";
import { FileInput } from "../../components/Form/Form.tsx";
import { useGet } from "../../components/Fetch/useFetch.tsx";
import { RoleEntity } from "../../Entities/RoleEntity.tsx";
import {
  PageContent,
  ScrollWrapper,
} from "../../components/Elements/Elements.tsx";
import PageTitle from "../../components/Elements/PageTitle/PageTitle.tsx";
import { IconExport, IconInfo } from "../../components/Icon/Icon.tsx";
import styles from "./ImportUtilisateurs.module.css";

const getInitialValues = () => ({
  file: undefined,
});

const validationSchema = object({
  file: requiredFile,
});

const prepareVariables = (values: { file: Blob }) => {
  const formData = new FormData();
  formData.append("file", values.file);

  return formData;
};

const ImportUtilisateurs = () => {
  const roles = useGet(url`/api/roles/`, {});
  if (!roles.isResolved) {
    return;
  }
  return (
    <>
      <PageTitle
        title="Import des données utilisateurs"
        icon={<IconExport />}
      />
      <PageContent>
        <ScrollWrapper>
          <Stepper
            getInitialValues={getInitialValues}
            prepareVariables={prepareVariables}
            validationSchema={validationSchema}
            steps={[
              {
                title: "Introduction",
                content: (
                  <div className={styles.instruction}>
                    Chaque étape vous permet d&apos;intégrer des données liées
                    aux utilisateurs. Certaines étapes sont obligatoires,
                    d&apos;autres non (présence du bouton &quot;Ignorer&quot;).
                    <br />A chaque étape, l&apos;icône&nbsp;
                    <span className={styles.icon}>
                      <IconExport />
                    </span>{" "}
                    vous permet de télécharger un fichier CSV d&apos;exemple,
                    vide, et le remplir avant de l&apos;envoyer.
                  </div>
                ),
                useFormik: false,
                skippable: false,
              },
              {
                title: "Import des utilisateurs",
                content: (
                  <>
                    <div className={styles.iconEtape}>
                      <a
                        data-tooltip-id="utilisateurs"
                        className={styles.export}
                      >
                        <IconInfo />
                      </a>
                      <a
                        href={url`/api/imports/empty-file-utilisateurs`}
                        className={styles.export}
                      >
                        <IconExport />
                      </a>
                    </div>

                    <Tooltip id="utilisateurs" place="left">
                      <span className={styles.detail}>
                        <span className={styles.title}>
                          Colonnes attendues :
                        </span>{" "}
                        <br />
                        username <span className={styles.obligatoire}>
                          *
                        </span>{" "}
                        <br />
                        email
                        <br />
                        password <span className={styles.obligatoire}>
                          *
                        </span>{" "}
                        <br />
                        <br />
                        <span className={styles.obligatoire}>*</span> :
                        obligatoire
                      </span>
                    </Tooltip>
                    <FormImportUtilisateursRoles />
                  </>
                ),
                useFormik: true,
                submitUrl: url`/api/imports/import-utilisateurs`,
                skippable: true,
              },
              {
                title: "Import des rôles",
                content: (
                  <>
                    <div className={styles.iconEtape}>
                      <a data-tooltip-id="roles" className={styles.export}>
                        <IconInfo />
                      </a>
                      <a
                        href={url`/api/imports/empty-file-roles`}
                        className={styles.export}
                      >
                        <IconExport />
                      </a>
                    </div>
                    <Tooltip id="roles" place="left">
                      <span className={styles.detail}>
                        <span className={styles.title}>
                          Colonnes attendues :
                        </span>{" "}
                        <br />
                        code <span className={styles.obligatoire}>*</span>
                        <br />
                        libelle <span className={styles.obligatoire}>*</span>
                        <br />
                        <br />
                        <span className={styles.obligatoire}>*</span> :
                        obligatoire
                      </span>
                    </Tooltip>

                    <div>
                      Les rôles suivants sont déjà en base :{" "}
                      <ul>
                        {roles?.data?.map((etl: RoleEntity, key: Key) => (
                          <li key={key}>
                            {etl.roleCode} ({etl.roleLibelle})
                          </li>
                        ))}
                      </ul>
                      <br />
                      Si vous ne voulez pas ajouter d&apos;autres rôles, vous
                      pouvez ignorer cette étape.
                      <br />
                    </div>
                    <FormImportUtilisateursRoles />
                  </>
                ),
                useFormik: true,
                submitUrl: url`/api/imports/import-roles`,
                skippable: true,
              },
              {
                title: "Import des liens utilisateurs / rôles",
                content: (
                  <>
                    <div className={styles.iconEtape}>
                      <a
                        data-tooltip-id="utilisateurs-roles"
                        className={styles.export}
                      >
                        <IconInfo />
                      </a>
                      <a
                        href={url`/api/imports/empty-file-utilisateurs-roles`}
                        className={styles.export}
                      >
                        <IconExport />
                      </a>
                    </div>
                    <Tooltip id="utilisateurs-roles" place="left">
                      <span className={styles.detail}>
                        <span className={styles.title}>
                          Colonnes attendues :
                        </span>{" "}
                        <br />
                        roleCode <span className={styles.obligatoire}>*</span>
                        <br />
                        username <span className={styles.obligatoire}>*</span>
                        <br />
                        <br />
                        <span className={styles.obligatoire}>*</span> :
                        obligatoire
                      </span>
                    </Tooltip>
                    <FormImportUtilisateursRoles />
                  </>
                ),
                useFormik: true,
                submitUrl: url`/api/imports/import-utilisateurs-roles`,
                skippable: false,
              },
              {
                title: "Imports terminés",
                content: (
                  <div className={styles.instruction}>
                    Les utilisateurs / rôles ont été importés.
                  </div>
                ),
                useFormik: false,
                skippable: false,
              },
            ]}
          />
        </ScrollWrapper>
      </PageContent>
    </>
  );
};

export default ImportUtilisateurs;

const FormImportUtilisateursRoles = () => {
  const { setFieldValue } = useFormikContext();

  return (
    <>
      <FileInput
        name={"file"}
        label={"Choisissez un fichier csv"}
        accept={".csv"}
        onChange={(e) => {
          setFieldValue("file", e.target.files[0]);
        }}
        className={undefined}
      />
    </>
  );
};
